/* eslint-disable arrow-body-style */

import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, exhaustMap, map, mergeMap, of} from "rxjs";
import {FxActions} from "../shared.actions";
import {FxService} from "../../services/fx/fx.service";
import {ErrorHandlingUtil} from "../../utils/error-handling-util";

export const loadCurrencyList = createEffect(
    (actions$ = inject(Actions), fxService = inject(FxService)) => {
        return actions$.pipe(
            ofType(FxActions.loadCurrencyList),
            exhaustMap(() =>
                fxService.getCurrencies().pipe(
                    map((currencyList) => FxActions.loadCurrencyListSuccess({currencyList})),
                    catchError((errorResponse: any) =>
                        of(FxActions.loadCurrencyListError({
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse) || "Could not load currency list - please reload the currencies"
                        })))
                )
            )
        );
    },
    {functional: true}
);

export const convertCurrency = createEffect(
    (actions$ = inject(Actions), fxService = inject(FxService)) => {
        return actions$.pipe(
            ofType(FxActions.convertFx),
            exhaustMap((action) =>
                fxService.getLatestExchangeRates(action.from, action.to, action.amount).pipe(
                    map((exchangeRate) => FxActions.convertFxSuccess({exchangeRate})),
                    catchError((errorResponse: any) => {
                        return of(FxActions.convertFxError({
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse) || "Conversion not possible - please reload"
                        }))
                    })
                )
            )
        );
    },
    {functional: true}
);

// Inline FX autofill (US-1171). mergeMap (not exhaustMap) so multiple distinct currency pairs fetch in
// parallel; the cap keeps a many-currency deal from firing dozens of simultaneous Frankfurter calls.
// Dispatch-level dedupe (one call per pair) is handled by the dispatching components.
export const fetchAutofillRate = createEffect(
    (actions$ = inject(Actions), fxService = inject(FxService)) => {
        return actions$.pipe(
            ofType(FxActions.fetchAutofillRate),
            mergeMap(({from, to}) =>
                fxService.getLatestExchangeRates(from, to, 1).pipe(
                    map((exchangeRate) => FxActions.fetchAutofillRateSuccess({
                        from,
                        to,
                        fraction: exchangeRate.rates?.[to] ?? null,
                        asOfDate: exchangeRate.date ?? null
                    })),
                    catchError((errorResponse: any) =>
                        of(FxActions.fetchAutofillRateError({
                            from,
                            to,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        })))
                ), 6)
        );
    },
    {functional: true}
);

// Chosen-date FX lookups for the deal FX manager (US-1172). mergeMap with a cap so several pairs
// fetch in parallel; keyed by `${date}_${from}_${to}` so multiple pairs at one date coexist.
// Dispatch-level dedupe (one call per pair) is handled by the dispatching component.
export const loadHistoricalExchangeRates = createEffect(
    (actions$ = inject(Actions), fxService = inject(FxService)) => {
        return actions$.pipe(
            ofType(FxActions.loadHistoricalExchangeRates),
            mergeMap(({date, from, to}) =>
                fxService.getHistoricalExchangeRates(date, from, to).pipe(
                    map((exchangeRate) => FxActions.loadHistoricalExchangeRatesSuccess({
                        date,
                        from,
                        to,
                        fraction: exchangeRate.rates?.[to] ?? null,
                        asOfDate: exchangeRate.date ?? null
                    })),
                    catchError((errorResponse: any) =>
                        of(FxActions.loadHistoricalExchangeRatesError({
                            date,
                            from,
                            to,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        })))
                ), 6)
        );
    },
    {functional: true}
);
